import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../store/reducers/appSlice';
import { deleteShop } from '../../lib/api/shop/delete_shop';
import { submitLogToBugsnag } from '../../lib/api/log';
import MainSquareButton from '../../ui/buttons/MainSquareButton';

interface DeleteBrandFormProps {
  shopSlug: string;
  shopName: string;
  /** True when this is the brand the user is currently working in. */
  isActive: boolean;
  onClose: () => void;
  /** Called after the brand is gone server-side, so the list can update. */
  onDeleted: (shopSlug: string) => void;
}

const DeleteBrandForm: React.FC<DeleteBrandFormProps> = ({
  shopSlug,
  shopName,
  isActive,
  onClose,
  onDeleted,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const confirmDelete = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const res = await deleteShop(shopSlug);

      if (!res.success) {
        throw new Error(res.message || 'Failed to delete brand');
      }

      dispatch(
        setMessage({
          message: `${shopName} deleted.`,
          messageType: 'success',
        })
      );
      onDeleted(shopSlug);
      onClose();
    } catch (error: any) {
      console.error('Error deleting brand:', error);
      submitLogToBugsnag('error', `Error deleting brand: ${error}`);
      dispatch(
        setMessage({
          message: error.message || 'Failed to delete brand',
          messageType: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='deleteBrandForm'>
      <h3 className='deleteBrandForm__title'>Delete brand</h3>
      <p className='deleteBrandForm__body'>
        Delete <strong>{shopName}</strong>? It disappears from your brand list
        and its scheduled reports and automations stop running. Connected
        platforms and history are kept, so support can restore it.
      </p>
      {isActive && (
        <p className='deleteBrandForm__note'>
          This is your active brand — you&apos;ll be switched to another one.
        </p>
      )}
      <div className='deleteBrandForm__actions'>
        <button
          type='button'
          className='deleteBrandForm__cancel'
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <MainSquareButton
          type='button'
          value='Delete brand'
          normal={true}
          buttonType='delete'
          loading={loading}
          onClick={confirmDelete}
        />
      </div>
    </div>
  );
};

export default DeleteBrandForm;
